import React from 'react';
import { PlayerProfile, PlayerData, PlayerInjury, PlayerTransfer, PlayerAchievement, PlayerStats } from '../../types/player';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { metersToFeetAndInches } from '../../utils/playerDataUtils';
import { cn } from '../../lib/utils';
import { InjuryInfoSection } from './InjuryInfoSection';
import { SpiderChart } from './SpiderChart';
import { PlayerPerformanceCard } from './PlayerPerformanceCard';
import { usePlayerCurrentSeasonStats, usePlayerClubLeagueInfo } from '../../hooks/usePlayerDetails';
import { PlayerAvailabilityTimeline } from './PlayerAvailabilityTimeline';
import { PlayerCareerStats } from './PlayerCareerStats';
import { AddToWishlistButton } from '../AddToWishlistButton';
import { ExportReportButton } from './ExportReportButton';
import { AthleteIntelligenceScoreDial } from './AthleteIntelligenceScoreDial';
import { 
  calculateGoalInvolvementPer90, 
  calculateDisciplineRiskIndex, 
  calculateMinutesPerGoal, 
  calculateMinutesPerAssist,
  formatStat
} from '../../utils/playerAdvancedStatsUtils';

interface CategoryData {
  category_name: string;
  profile_content: any;
  processing_status: 'completed' | 'failed';
  error_message?: string;
}

interface PlayerInfoTabsProps {
  playerProfile: PlayerProfile;
  playerData: PlayerData | null;
  playerInjuries: PlayerInjury[] | null;
  playerTransfers: PlayerTransfer[] | null;
  playerAchievements: PlayerAchievement[] | null;
  playerStats: PlayerStats[] | null;
  isLoading: boolean;
  completedCategories?: CategoryData[];
  onScrollToAnalysis?: () => void;
}

export const PlayerInfoTabs: React.FC<PlayerInfoTabsProps> = ({
  playerProfile,
  playerData,
  playerInjuries,
  playerTransfers,
  playerAchievements,
  playerStats,
  isLoading,
  completedCategories = [],
  onScrollToAnalysis
}) => {
  // Use the hook to get current season stats based on player's current club and league
  const { data: currentSeasonStats, isLoading: isLoadingCurrentStats } = 
    usePlayerCurrentSeasonStats(playerProfile.id);
  
  // Use the hook to get club league information
  const { data: clubLeagueInfo, isLoading: isLoadingClubLeagueInfo } =
    usePlayerClubLeagueInfo(playerProfile.id);
  
  // Check if there are active injuries
  const hasActiveInjuries = playerInjuries && 
    playerInjuries.some(injury => !injury.until_date || new Date(injury.until_date) > new Date());

  return (
    <div className="p-6">
      {/* Player Image and Name Header */}
      <div className="mb-6 pb-6 border-b border-gray-700">
        <div className="flex flex-row items-start gap-6">
          {/* Left side - Player Photo */}
          <div className="relative">
            <div className="border border-gray-500 rounded-lg shadow-lg overflow-hidden p-[5px] bg-black">
              <img 
                src={playerProfile.image_url || '/footballer.png'} 
                alt={playerProfile.name}
                className="w-48 h-48 object-cover rounded-sm"
              />
            </div>
            
            {/* Overall Player Score Badge - Only show if we have player data and a score */}
            {playerData && playerData.overall_player_score !== undefined && (
              <div className="absolute -bottom-4 -right-4 bg-[#121212] rounded-full shadow-lg p-1 border-2 border-[#4E46FF]">
                {isLoading ? (
                  <div className="w-14 h-14 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#4E46FF] border-t-transparent"></div>
                  </div>
                ) : (
                  <div className={cn(
                    "w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold",
                    playerData.overall_player_score >= 7 ? "bg-[#26222E] text-green-400" :
                    playerData.overall_player_score >= 4 ? "bg-[#26222E] text-yellow-400" :
                    "bg-[#26222E] text-red-400"
                  )}>
                    {playerData.overall_player_score}
                  </div>
                )}
              </div>
            )}
            
            {/* Active Injury Badge */}
            {hasActiveInjuries && (
              <div className="absolute -top-3 -left-3 bg-red-500 text-[#CECFD2] px-3 py-1 rounded-md text-xs font-bold shadow-lg">
                INJURY RISK
              </div>
            )}
          </div>
          
          {/* Middle - Player Info */}
          <div className="flex-1">
            <div className="flex flex-col">
              <h3 className="text-2xl font-bold text-[#CECFD2] mb-1">{playerProfile.name}</h3>
              <p className="text-[#CECFD2] mb-2">{playerProfile.full_name}</p>
              
              <div className="mb-4">
                <p className="text-sm font-medium text-[#4E46FF]">
                  {playerProfile.position || 'Unknown Position'}
                  {playerProfile.other_positions && ` (Also: ${playerProfile.other_positions})`}
                </p>
                
                {playerProfile.club_name && (
                  <p className="text-sm text-[#CECFD2] mt-1">
                    {playerProfile.club_name}
                    {isLoadingClubLeagueInfo ? (
                      <span className="text-xs ml-1">Loading league info...</span>
                    ) : clubLeagueInfo && clubLeagueInfo.league_name ? (
                      <span className="text-xs ml-1">
                        - {clubLeagueInfo.league_name}
                        {clubLeagueInfo.country_name && `, ${clubLeagueInfo.country_name}`}
                      </span>
                    ) : null}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* Right side - AIS Dial and Buttons */}
          <div className="flex flex-row items-start gap-4">
            {/* AIS Dial Card */}
            <AthleteIntelligenceScoreDial
              completedCategories={completedCategories}
              onScrollToAnalysis={onScrollToAnalysis || (() => {})}
              className=""
            />
            
            {/* Action Buttons */}
            <div className="flex flex-col gap-2">
              <AddToWishlistButton 
                playerId={playerProfile.id}
                variant="outline"
                size="sm"
              />
            </div>
          </div>
        </div>
        
        {/* Active Injury Details */}
        {hasActiveInjuries && (
          <div className="mt-4 w-full max-w-2xl">
            <InjuryInfoSection 
              playerInjuries={playerInjuries!}
              playerId={playerProfile.id}
              playerName={playerProfile.name}
              playerClub={playerProfile.club_name || 'Unknown'}
            />
          </div>
        )}
      </div>

      {/* Tabbed Information Section */}
      <Tabs defaultValue="current-stats" className="w-full">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="current-stats">Current Season Stats</TabsTrigger>
          <TabsTrigger value="player-info">Player Information</TabsTrigger>
          <TabsTrigger value="availability">Availability Timeline</TabsTrigger>
        </TabsList>
        
        {/* Current Season Stats Tab */}
        <TabsContent value="current-stats" className="space-y-4">
          <div className="bg-[#121212] rounded-lg shadow-lg p-4 border border-gray-500">
            {isLoadingCurrentStats ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#4E46FF] border-t-transparent"></div>
              </div>
            ) : !currentSeasonStats ? (
              <p className="text-center text-[#CECFD2] py-4">No current season statistics available</p>
            ) : (
              <div className="space-y-4">
                <h4 className="text-xl font-bold text-[#CECFD2] mb-3">
                  {currentSeasonStats.competition_name} ({currentSeasonStats.season_id})
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Stats Grid */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Club</p>
                        <p className="text-xl font-bold text-[#CECFD2]">{playerProfile.club_name || 'Unknown'}</p>
                      </div>
                      <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Appearances</p>
                        <p className="text-xl font-bold text-[#CECFD2]">{currentSeasonStats.appearances}</p>
                      </div>
                      <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Goals</p>
                        <p className="text-xl font-bold text-[#CECFD2]">{currentSeasonStats.goals}</p>
                      </div>
                      <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Assists</p>
                        <p className="text-xl font-bold text-[#CECFD2]">{currentSeasonStats.assists}</p>
                      </div>
                      <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Yellow Cards</p>
                        <p className="text-xl font-bold text-[#CECFD2]">{currentSeasonStats.yellow_cards}</p>
                      </div>
                      <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Red Cards</p>
                        <p className="text-xl font-bold text-[#CECFD2]">{currentSeasonStats.red_cards}</p>
                      </div>
                      <div className="bg-[#26222E] rounded-lg p-4 text-center col-span-2 border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                        <p className="text-sm text-[#CECFD2]">Minutes Played</p>
                        <p className="text-xl font-bold text-[#CECFD2]">
                          {currentSeasonStats.minutes_played ? currentSeasonStats.minutes_played.toLocaleString() : '0'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Spider Chart */}
                  <div className="bg-[#26222E] rounded-lg p-4 border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                    <h5 className="text-sm font-medium text-[#CECFD2] mb-2 text-center">Performance Radar</h5>
                    <SpiderChart playerStats={currentSeasonStats} />
                  </div>
                </div>
                
                {/* Advanced Statistics Section */}
                <div className="mt-6">
                  <h4 className="text-xl font-bold text-[#CECFD2] mb-3">Current Season Advanced Statistics</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                      <p className="text-sm text-[#CECFD2]">Goal Involvement per 90</p>
                      <p className="text-xl font-bold text-[#CECFD2]">
                        {formatStat(calculateGoalInvolvementPer90(currentSeasonStats))}
                      </p>
                    </div>
                    <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                      <p className="text-sm text-[#CECFD2]">Discipline Risk Index</p>
                      <p className="text-xl font-bold text-[#CECFD2]">
                        {formatStat(calculateDisciplineRiskIndex(currentSeasonStats))}
                      </p>
                    </div>
                    <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                      <p className="text-sm text-[#CECFD2]">Minutes per Goal</p>
                      <p className="text-xl font-bold text-[#CECFD2]">
                        {formatStat(calculateMinutesPerGoal(currentSeasonStats))}
                      </p>
                    </div>
                    <div className="bg-[#26222E] rounded-lg p-4 text-center border border-gray-500 hover:border-[#4E46FF] transition-colors duration-200">
                      <p className="text-sm text-[#CECFD2]">Minutes per Assist</p>
                      <p className="text-xl font-bold text-[#CECFD2]">
                        {formatStat(calculateMinutesPerAssist(currentSeasonStats))}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Career Stats Section */}
                <div className="mt-8 pt-6 border-t border-gray-700">
                  <PlayerCareerStats playerStats={playerStats} isLoading={isLoadingCurrentStats} />
                </div>
              </div>
            )}
          </div>
        </TabsContent>
        
        {/* Player Information Tab */}
        <TabsContent value="player-info" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Information */}
            <div className="bg-[#121212] rounded-lg shadow-lg p-4 border border-gray-500">
              <h4 className="text-lg font-bold text-[#CECFD2] mb-4 border-b border-gray-700 pb-2">Personal Information</h4>
              <table className="w-full">
                <tbody className="divide-y divide-gray-700">
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Age</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.age || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Date of Birth</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.date_of_birth || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Place of Birth</td>
                    <td className="py-3 text-sm font-medium text-white">
                      {playerProfile.place_of_birth_city ? 
                        `${playerProfile.place_of_birth_city}${playerProfile.place_of_birth_country ? ', ' + playerProfile.place_of_birth_country : ''}` : 
                        'Unknown'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Nationality</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.nationality || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Height</td>
                    <td className="py-3 text-sm font-medium text-white">
                      {playerProfile.height ? (
                        <>
                          {playerProfile.height.replace(',', '.')} ({metersToFeetAndInches(playerProfile.height)})
                        </>
                      ) : (
                        'Not available'
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Foot</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.foot}</td>
                  </tr>
                  {playerProfile.agent_name && (
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Agent</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.agent_name}</td>
                  </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Club Information */}
            <div className="bg-[#121212] rounded-lg shadow-lg p-4 border border-gray-500">
              <h4 className="text-lg font-bold text-[#CECFD2] mb-4 border-b border-gray-700 pb-2">Club Information</h4>
              <table className="w-full">
                <tbody className="divide-y divide-gray-700">
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Current Club</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.club_name || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Position</td>
                    <td className="py-3 text-sm font-medium text-white">
                      <span className="px-2 py-1 bg-[#4E46FF] rounded-md text-xs font-medium text-white inline-block">
                        {playerProfile.position || 'Unknown'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Other Positions</td>
                    <td className="py-3 text-sm font-medium text-white">
                      {playerProfile.other_positions ? (
                        <span className="px-2 py-1 bg-[#26222E] rounded-md text-xs font-medium text-white inline-block">
                          {playerProfile.other_positions}
                        </span>
                      ) : 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Shirt Number</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.shirt_number || 'N/A'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Joined On</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.joined_on || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Contract Until</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.contract_expires || 'Unknown'}</td>
                  </tr>
                  <tr>
                    <td className="py-3 text-sm text-gray-400">Market Value</td>
                    <td className="py-3 text-sm font-medium text-white">{playerProfile.market_value || 'Unknown'}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
        
        {/* Availability Timeline Tab */}
        <TabsContent value="availability" className="space-y-4">
          <div className="bg-[#121212] rounded-lg shadow-lg p-4 border border-gray-500">
            <PlayerAvailabilityTimeline playerId={playerProfile.id} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};